import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Info, Loader2, Eye, Users, CalendarDays, MousePointerClick, Clock, ScrollText,
  Monitor, Smartphone, Tablet, Bot, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const DEVICE_META = [
  ["desktop", "Desktop", Monitor],
  ["mobile", "Smartphone", Smartphone],
  ["tablet", "Tablet", Tablet],
];
const VISITOR_META = [
  ["nuovi", "Nuovi", "#009DDF"],
  ["di_ritorno", "Di ritorno", "#1B4268"],
  ["abituali", "Abituali", "#FFC107"],
];

const Tip = ({ text }) => (
  <span className="inline-flex align-middle ml-1 text-ink/30 cursor-help" title={text}>
    <Info size={13} />
  </span>
);

const fmtTime = (sec) => {
  if (!sec) return "0s";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m ? `${m}m ${s}s` : `${s}s`;
};

const HBar = ({ label, value, max, color }) => (
  <div className="flex items-center gap-3">
    <span className="w-24 shrink-0 text-xs text-ink/60">{label}</span>
    <div className="flex-1 h-6 rounded-lg bg-ink/5 overflow-hidden">
      <div
        className="h-full rounded-lg transition-all duration-700"
        style={{ width: `${max ? Math.max(3, (value / max) * 100) : 0}%`, backgroundColor: color }}
      />
    </div>
    <span className="w-10 text-right text-sm font-semibold text-ink">{value}</span>
  </div>
);

const Section = ({ title, tip, children, testid }) => (
  <section className="bg-white rounded-2xl border border-ink/10 p-6" data-testid={testid}>
    <h3 className="font-serif font-bold text-lg mb-5">
      {title}
      {tip && <Tip text={tip} />}
    </h3>
    {children}
  </section>
);

export default function StatisticsAdmin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("sf-admin-token") || "";
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const url = d === 1 ? `${API}/admin/analytics?hours=24` : `${API}/admin/analytics?days=${d}`;
      const { data: res } = await axios.get(url, auth);
      setData(res);
    } catch {
      toast.error("Errore nel caricamento delle statistiche");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const growth = data?.totals?.growth_pct;
  const GrowthBadge = () => {
    if (growth === null || growth === undefined) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 bg-ink/5 text-ink/50" data-testid="stats-growth-badge">
          <Minus size={12} /> n.d.
        </span>
      );
    }
    const up = growth >= 0;
    return (
      <span
        className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2.5 py-1 ${up ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}
        data-testid="stats-growth-badge"
      >
        {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        {up ? "+" : ""}{growth}%
      </span>
    );
  };

  const cards = data
    ? [
        ["Visite totali", data.totals.views, Eye, "stats-card-visits", "Pagine viste nel periodo, esclusi i bot", true],
        ["Visitatori unici", data.totals.unique_visitors, Users, "stats-card-visitors", "Persone distinte (identificatore anonimo), esclusi i bot", false],
        ["Visite oggi", data.totals.views_today, CalendarDays, "stats-card-today", "Pagine viste nella giornata di oggi", false],
        ["Pagine per visita", data.totals.pages_per_visit, MousePointerClick, "stats-card-pages-per-visit", "Quante pagine vede in media ogni visitatore", false],
        ["Tempo medio", fmtTime(data.totals.avg_time_sec), Clock, "stats-card-avg-time", "Tempo medio di permanenza su una pagina", false],
        ["Scroll medio", `${Math.round(data.totals.avg_scroll_pct)}%`, ScrollText, "stats-card-avg-scroll", "Quanto in basso scorrono in media i visitatori", false],
      ]
    : [];

  const vtMax = data ? Math.max(1, data.visitor_types.nuovi, data.visitor_types.di_ritorno, data.visitor_types.abituali) : 1;
  const devMax = data ? Math.max(1, data.devices.desktop, data.devices.mobile, data.devices.tablet) : 1;
  const weekdayData = data ? data.by_weekday.map((v, i) => ({ name: WEEKDAYS[i], visite: v })) : [];
  const hourData = data ? data.by_hour.map((v, i) => ({ name: `${i}`, visite: v })) : [];
  const is24h = days === 1;
  const hourlySeries =
    data && is24h
      ? (() => {
          const startH = (new Date().getHours() + 1) % 24;
          return Array.from({ length: 24 }, (_, i) => {
            const h = (startH + i) % 24;
            return { name: `${String(h).padStart(2, "0")}:00`, visite: data.by_hour[h] };
          });
        })()
      : [];

  return (
    <div className="space-y-6" data-testid="statistics-admin">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif font-bold text-3xl">Statistiche del sito</h1>
          <p className="text-sm text-ink/50 mt-1">
            Statistiche interne first-party: nessun cookie di terze parti, contano anche chi rifiuta i cookie. I bot sono esclusi.
          </p>
        </div>
        <div className="flex gap-2" data-testid="stats-period-selector">
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`stats-period-${d}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${
                days === d ? "bg-ink text-white" : "bg-white text-ink/60 border border-ink/15 hover:text-ink"
              }`}
            >
              {d === 1 ? "24 ore" : `${d} giorni`}
            </button>
          ))}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-azure" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="stats-cards">
            {cards.map(([label, value, Icon, testid, tip, showGrowth]) => (
              <div key={testid} className="bg-white rounded-2xl border border-ink/10 p-4" data-testid={testid}>
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-azure/10 text-brandblue">
                    <Icon size={15} />
                  </span>
                  {showGrowth && <GrowthBadge />}
                </div>
                <p className="font-serif font-bold text-2xl text-ink">{value}</p>
                <p className="text-[11px] text-ink/50 mt-0.5">
                  {label}
                  <Tip text={tip} />
                </p>
              </div>
            ))}
          </div>

          <Section
            title={is24h ? "Andamento visite, ora per ora" : "Andamento visite"}
            tip={is24h ? "Visite per fascia oraria nelle ultime 24 ore, in ordine cronologico" : "Visite e visitatori unici per giorno"}
            testid="stats-chart-daily"
          >
            <div className="h-64">
              {is24h ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlySeries} margin={{ top: 5, right: 8, left: -18, bottom: 0 }} data-testid="stats-chart-hourly">
                    <CartesianGrid strokeDasharray="3 3" stroke="#0C1B2A10" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#0C1B2A80" }} tickLine={false} axisLine={false} interval={2} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#0C1B2A80" }} tickLine={false} axisLine={false} />
                    <Tooltip formatter={(v) => [v, "Visite"]} labelFormatter={(h) => `Ore ${h}`} contentStyle={{ borderRadius: 12, border: "1px solid #0C1B2A15", fontSize: 12 }} />
                    <Bar dataKey="visite" fill="#009DDF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#009DDF" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#009DDF" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#0C1B2A10" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={(d) => d.slice(5).split("-").reverse().join("/")} tick={{ fontSize: 11, fill: "#0C1B2A80" }} tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#0C1B2A80" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(v, name) => [v, name === "views" ? "Visite" : "Visitatori"]}
                    labelFormatter={(d) => d.split("-").reverse().join("/")}
                    contentStyle={{ borderRadius: 12, border: "1px solid #0C1B2A15", fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="views" stroke="#009DDF" strokeWidth={2.5} fill="url(#gv)" name="views" />
                  <Area type="monotone" dataKey="visitors" stroke="#1B4268" strokeWidth={1.5} strokeDasharray="5 4" fill="none" name="visitors" />
                </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </Section>

          <div className="grid lg:grid-cols-3 gap-6">
            <Section title="Tipo di visitatori" tip="Nuovi: prima visita in assoluto. Di ritorno: già visti prima del periodo. Abituali: 2+ pagine viste nel periodo" testid="stats-visitor-types">
              <div className="space-y-3">
                {VISITOR_META.map(([key, label, color]) => (
                  <HBar key={key} label={label} value={data.visitor_types[key]} max={vtMax} color={color} />
                ))}
              </div>
            </Section>

            <Section title="Base storica" tip="Visitatori unici conosciuti di sempre. Dormienti: conosciuti ma inattivi nel periodo" testid="stats-base">
              <div className="space-y-4">
                <div>
                  <p className="font-serif font-bold text-3xl text-ink">{data.base.all_time_unique}</p>
                  <p className="text-xs text-ink/50">visitatori unici totali di sempre</p>
                </div>
                <div>
                  <p className="font-serif font-bold text-3xl text-gold">{data.base.dormienti}</p>
                  <p className="text-xs text-ink/50">dormienti (non tornati nel periodo)</p>
                </div>
              </div>
            </Section>

            <Section title="Dispositivi" tip="Visite per tipo di dispositivo, rilevato dal browser" testid="stats-devices">
              <div className="space-y-3">
                {DEVICE_META.map(([key, label, Icon]) => (
                  <div key={key} className="flex items-center gap-3">
                    <Icon size={15} className="text-brandblue shrink-0" />
                    <div className="flex-1">
                      <HBar label={label} value={data.devices[key]} max={devMax} color="#009DDF" />
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {!is24h && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Section title="Giorni della settimana" tip="Visite totali per giorno della settimana" testid="stats-weekday">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weekdayData} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#0C1B2A10" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#0C1B2A80" }} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#0C1B2A80" }} tickLine={false} axisLine={false} />
                    <Tooltip formatter={(v) => [v, "Visite"]} contentStyle={{ borderRadius: 12, border: "1px solid #0C1B2A15", fontSize: 12 }} />
                    <Bar dataKey="visite" fill="#1B4268" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title="Fasce orarie" tip="Visite per ora del giorno (0–23), fuso orario italiano" testid="stats-hours">
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourData} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#0C1B2A10" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#0C1B2A80" }} tickLine={false} axisLine={false} interval={2} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#0C1B2A80" }} tickLine={false} axisLine={false} />
                    <Tooltip formatter={(v) => [v, "Visite"]} labelFormatter={(h) => `Ore ${h}:00`} contentStyle={{ borderRadius: 12, border: "1px solid #0C1B2A15", fontSize: 12 }} />
                    <Bar dataKey="visite" fill="#009DDF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>
          </div>
          )}

          <Section title="Bot e crawler" tip="Visite automatiche escluse dalle statistiche" testid="stats-bots">
            <p className="text-sm text-ink/60 leading-relaxed mb-5">
              I bot sono <strong>programmi automatici, non persone</strong>: i crawler di Google che indicizzano il sito,
              le anteprime di WhatsApp/Facebook quando qualcuno condivide un link, i monitor di disponibilità e i crawler
              SEO/intelligenza artificiale. Le loro visite sono <strong>escluse da tutti i numeri qui sopra</strong> e mostrate solo in questo riquadro.
            </p>
            <div className="flex flex-wrap gap-6 mb-5">
              <div>
                <p className="font-serif font-bold text-2xl text-ink" data-testid="stats-bot-hits">{data.bots.hits}</p>
                <p className="text-xs text-ink/50">accessi bot nel periodo</p>
              </div>
              <div>
                <p className="font-serif font-bold text-2xl text-ink" data-testid="stats-bot-distinct">{data.bots.distinct}</p>
                <p className="text-xs text-ink/50">bot distinti</p>
              </div>
            </div>
            {data.bots.ranking.length > 0 ? (
              <div className="space-y-2" data-testid="stats-bot-ranking">
                {data.bots.ranking.map((b) => (
                  <HBar key={b.name} label={b.name} value={b.hits} max={data.bots.ranking[0].hits} color="#FFC107" />
                ))}
              </div>
            ) : (
              <p className="text-sm text-ink/40">Nessun bot rilevato nel periodo.</p>
            )}
          </Section>

          <div className="grid lg:grid-cols-2 gap-6">
            <Section title="Pagine più viste" tip="Le pagine con più visite nel periodo" testid="stats-top-pages">
              {data.top_pages.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-ink/40 border-b border-ink/10">
                      <th className="pb-2 font-medium">Pagina</th>
                      <th className="pb-2 font-medium text-right">Visite</th>
                      <th className="pb-2 font-medium text-right">Visitatori</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_pages.map((p) => (
                      <tr key={p.path} className="border-b border-ink/5 last:border-0">
                        <td className="py-2.5 pr-3 font-mono text-xs text-ink/80 truncate max-w-[220px]">{p.path}</td>
                        <td className="py-2.5 text-right font-semibold">{p.views}</td>
                        <td className="py-2.5 text-right text-ink/60">{p.unique_visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-ink/40">Nessuna visita nel periodo.</p>
              )}
            </Section>

            <Section title="Provenienza (referrer)" tip="Siti esterni da cui arrivano i visitatori. Vuoto = accesso diretto" testid="stats-top-referrers">
              {data.top_referrers.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-ink/40 border-b border-ink/10">
                      <th className="pb-2 font-medium">Sorgente</th>
                      <th className="pb-2 font-medium text-right">Visite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_referrers.map((r) => (
                      <tr key={r.source} className="border-b border-ink/5 last:border-0">
                        <td className="py-2.5 pr-3 text-ink/80">{r.source}</td>
                        <td className="py-2.5 text-right font-semibold">{r.views}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-ink/40">Nessun referrer esterno nel periodo (visite dirette).</p>
              )}
            </Section>

            <Section title="Contatti ricevuti" tip="Richieste di valutazione arrivate nel periodo, per canale" testid="stats-leads">
              {data.leads && data.leads.total > 0 ? (
                <>
                  <p className="text-sm text-ink/60 mb-4">
                    Totale nel periodo: <span className="font-bold text-ink text-base" data-testid="stats-leads-total">{data.leads.total}</span>
                  </p>
                  <div className="space-y-2.5">
                    {data.leads.by_fonte.map((f) => (
                      <HBar
                        key={f.fonte}
                        label={f.fonte === "sito" ? "Sito — form contatti" : f.fonte.replace("landing-", "Landing · ").replace(/-/g, " ")}
                        value={f.count}
                        max={Math.max(...data.leads.by_fonte.map((x) => x.count))}
                        color="#16a34a"
                      />
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-ink/40">Nessun contatto nel periodo selezionato.</p>
              )}
            </Section>

            <Section title="Campagne (UTM)" tip="Visite da link con etichetta UTM, es. ?utm_source=facebook&utm_campaign=carosello-a. Aggiungila ai link che condividi sui social per misurarli" testid="stats-campaigns">
              {data.campaigns && data.campaigns.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-ink/40 border-b border-ink/10">
                      <th className="pb-2 font-medium">Campagna</th>
                      <th className="pb-2 font-medium text-right">Visite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.campaigns.map((c) => (
                      <tr key={c.label} className="border-b border-ink/5 last:border-0">
                        <td className="py-2.5 pr-3 text-ink/80">{c.label}</td>
                        <td className="py-2.5 text-right font-semibold">{c.views}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-ink/40">Nessuna visita con etichetta UTM nel periodo. Suggerimento: aggiungi <code className="text-xs bg-ink/5 px-1.5 py-0.5 rounded">?utm_source=facebook&utm_campaign=nome-post</code> ai link che pubblichi sui social.</p>
              )}
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
